import { useCallback, useEffect, useState } from 'react';
import { validateEmail } from '../utils';

// ajout des destinataires au chargement des données
const useAutoAddCustomerReceiver = ({
  ticket,
  recipientType,
  receiverList = [],
  draft = null,
  isEmailModifiedByUser,
  setErrorOnEmailValidation,
  setIsEmailModifiedByUser,
  setEmail,
  setReceiverList,
  isDraftSet,
  setReceiverMP,
  receiverMP,
  isMessageBoxHidden,
  setSubject,
  subject,
}) => {
  const [isReceiverSet, setIsReceiverSet] = useState(false);

  // Mémorisation de la fonction addReceiver
  const addReceiver = useCallback(
    (receiver) => {
      if (!receiver) return;

      const { isValid, email } = validateEmail(receiver);
      if (!isValid) {
        setErrorOnEmailValidation("L'adresse email n'est pas valide.");
        const timer = setTimeout(() => {
          setErrorOnEmailValidation(null);
        }, 3000);
        return () => clearTimeout(timer);
      }

      if (receiverList?.indexOf(email) !== -1) {
        if (isEmailModifiedByUser) {
          setErrorOnEmailValidation("L'adresse email est déjà ajoutée.");
          const timer = setTimeout(() => {
            setErrorOnEmailValidation(null);
          }, 3000);
          setIsEmailModifiedByUser(false);
          return () => clearTimeout(timer);
        }
        setEmail('');
      } else {
        setReceiverList((state) => [...state, email]);
        setEmail('');
      }
    },
    [
      receiverList,
      isEmailModifiedByUser,
      setErrorOnEmailValidation,
      setIsEmailModifiedByUser,
      setEmail,
      setReceiverList,
    ]
  );

  useEffect(() => {
    if (!isDraftSet) return;
    // vérification des conditions pour l'ajout automatique
    const autoAdd =
      import.meta.env.VITE_AUTO_ADD_CUSTOMER_AS_RECEIVER === 'true';
    // vérification du type de destinataire et du type d'envoi
    const canAddReceiver =
      autoAdd &&
      receiverList?.length === 0 &&
      (ticket?.lastReceivedFromContact || ticket?.contact?.email) &&
      ticket?.contactChannelType === 'MAIL' &&
      !draft;

    if (draft) {
      if (canAddReceiver && recipientType === 'CONTACT') {
        addReceiver(draft?.to);
        const ccRecievers = draft?.cc?.split(',');
        if (ccRecievers?.length > 0) {
          ccRecievers.forEach((cc) => {
            addReceiver(cc);
          });
        }
        setIsReceiverSet(true);
      }
    } else {
      if (canAddReceiver && recipientType === 'CONTACT') {
        addReceiver(ticket?.lastReceivedFromContact || ticket?.contact?.email);
        setIsReceiverSet(true);
      }
    }
  }, [recipientType, ticket, receiverList, draft, isDraftSet]);

  useEffect(() => {
    if (!isDraftSet) {
      return;
    } else {
      if (
        ticket?.contactChannelType === 'RABBITMQ' &&
        recipientType === 'CONTACT' &&
        receiverMP === null
      ) {
        const to = draft?.to || 'customer';
        setReceiverList([to]);
        setReceiverMP(to);
      }
    }
  }, [draft?.to, ticket?.contactChannelType]);

  // Synchronise le sujet et les destinataires lors du chargement du brouillon.
  useEffect(() => {
    if (!isDraftSet) return;
    if (isReceiverSet || isMessageBoxHidden) return;
    if (draft?.subject && !subject) {
      let subjectToParse = draft?.subject?.split(' ');
      subjectToParse?.shift();
      if (ticket?.order) {
        subjectToParse?.shift();
      }
      setSubject(subjectToParse?.join(' ') || '');
    }
    if (!receiverList?.length && (draft?.to || draft?.cc)) {
      const receiversToPush = [];
      if (draft?.to) {
        receiversToPush.unshift(draft?.to);
      }
      if (draft?.cc) {
        for (const elem of (draft?.cc ?? '').split(', ')) {
          receiversToPush.push(elem);
        }
      }
      for (const elem of receiversToPush) {
        addReceiver(elem);
        setIsReceiverSet(true);
      }
    }
  }, [
    isMessageBoxHidden,
    draft,
    ticket?.order,
    addReceiver,
    isDraftSet,
    subject,
    receiverList,
  ]);

  return {
    isReceiverSet,
    addReceiver,
  };
};

export default useAutoAddCustomerReceiver;
